import { useState } from "react";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { BiometricReferenceRange } from "@/lib/api_client";
import { GENDER_LABELS } from "@/lib/types";
import type { ReferenceType } from "@/lib/types";
import { useReferenceRanges, useCreateReferenceRange, useUpdateReferenceRange, useDeleteReferenceRange } from "@/hooks/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";

interface ReferencesTabV2Props {
  biometricId: string;
}

interface RangeGroup {
  key: string;
  gender: string;
  minAge: string;
  maxAge: string;
  normalMin: string;
  normalMax: string;
  optimalMin: string;
  optimalMax: string;
  ids: Partial<Record<ReferenceType, string>>;
  dirty: boolean;
  isNew: boolean;
}

function groupKey(gender: string, minAge: string, maxAge: string) {
  return `${gender || "all"}:${minAge}:${maxAge}`;
}

function buildGroups(ranges: BiometricReferenceRange[]): RangeGroup[] {
  const map = new Map<string, RangeGroup>();

  for (const r of ranges) {
    const g = r.gender ?? "";
    const k = groupKey(g, String(r.minAge), String(r.maxAge));
    if (!map.has(k)) {
      map.set(k, {
        key: k,
        gender: g,
        minAge: String(r.minAge),
        maxAge: String(r.maxAge),
        normalMin: "",
        normalMax: "",
        optimalMin: "",
        optimalMax: "",
        ids: {},
        dirty: false,
        isNew: false,
      });
    }
    const group = map.get(k)!;
    const rt = r.referenceType as ReferenceType;
    group.ids[rt] = r.id;
    if (rt === "normal_min") group.normalMin = String(r.value);
    if (rt === "normal_max") group.normalMax = String(r.value);
    if (rt === "optimal_min") group.optimalMin = String(r.value);
    if (rt === "optimal_max") group.optimalMax = String(r.value);
  }

  return Array.from(map.values());
}

export function ReferencesTabV2({ biometricId }: ReferencesTabV2Props) {
  const { data: allRanges, isLoading } = useReferenceRanges();
  const createRange = useCreateReferenceRange();
  const updateRange = useUpdateReferenceRange();
  const deleteRange = useDeleteReferenceRange();

  const ranges = (allRanges ?? []).filter((r) => r.biometricId === biometricId);

  const [groups, setGroups] = useState<RangeGroup[] | null>(null);
  const currentGroups = groups ?? buildGroups(ranges);

  const handleAddGroup = () => {
    const k = `new-${Date.now()}`;
    setGroups([
      ...currentGroups,
      {
        key: k,
        gender: "",
        minAge: "18",
        maxAge: "120",
        normalMin: "",
        normalMax: "",
        optimalMin: "",
        optimalMax: "",
        ids: {},
        dirty: true,
        isNew: true,
      },
    ]);
  };

  const update = (idx: number, field: Partial<RangeGroup>) => {
    setGroups(
      currentGroups.map((g, i) => (i === idx ? { ...g, ...field, dirty: true } : g))
    );
  };

  const handleSaveGroup = async (idx: number) => {
    const group = currentGroups[idx]!;
    const base = {
      biometricId,
      gender: group.gender || null,
      minAge: group.minAge,
      maxAge: group.maxAge,
    };

    const pairs: { type: ReferenceType; value: string }[] = [
      { type: "normal_min", value: group.normalMin },
      { type: "normal_max", value: group.normalMax },
      { type: "optimal_min", value: group.optimalMin },
      { type: "optimal_max", value: group.optimalMax },
    ];

    const newIds = { ...group.ids };

    for (const { type, value } of pairs) {
      const existingId = group.ids[type];
      if (value !== "") {
        if (existingId) {
          await updateRange.mutateAsync({ id: existingId, data: { ...base, referenceType: type, value } });
        } else {
          const created = await createRange.mutateAsync({ ...base, referenceType: type, value });
          newIds[type] = created.id;
        }
      } else if (existingId) {
        await deleteRange.mutateAsync(existingId);
        delete newIds[type];
      }
    }

    setGroups(
      currentGroups.map((g, i) =>
        i === idx ? { ...g, ids: newIds, dirty: false, isNew: false } : g
      )
    );
    toast.success("Referansegruppe lagret");
  };

  const handleDeleteGroup = async (idx: number) => {
    const group = currentGroups[idx]!;
    for (const id of Object.values(group.ids)) {
      if (id) await deleteRange.mutateAsync(id);
    }
    setGroups(currentGroups.filter((_, i) => i !== idx));
    toast.success("Referansegruppe slettet");
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-admin-text-secondary">
          Referanseomrader ({currentGroups.length} grupper)
        </h3>
        <Button variant="outline" size="sm" onClick={handleAddGroup}>
          <Plus className="h-3 w-3" />
          Legg til gruppe
        </Button>
      </div>

      {currentGroups.length === 0 && (
        <p className="text-center text-admin-text-subtle py-8">
          Ingen referanseomrader definert
        </p>
      )}

      <div className="grid gap-3">
        {currentGroups.map((group, idx) => (
          <Card key={group.key}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 flex-1">
                  <Select
                    value={group.gender}
                    onChange={(e) => update(idx, { gender: e.target.value })}
                    className="w-28"
                  >
                    <option value="">Alle</option>
                    {Object.entries(GENDER_LABELS).map(([val, label]) => (
                      <option key={val} value={val}>{label}</option>
                    ))}
                  </Select>
                  <span className="text-admin-text-subtle text-sm">,</span>
                  <Input
                    type="number"
                    value={group.minAge}
                    onChange={(e) => update(idx, { minAge: e.target.value })}
                    className="w-16"
                  />
                  <span className="text-admin-text-subtle text-sm">—</span>
                  <Input
                    type="number"
                    value={group.maxAge}
                    onChange={(e) => update(idx, { maxAge: e.target.value })}
                    className="w-16"
                  />
                  <span className="text-admin-text-subtle text-sm">ar</span>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleSaveGroup(idx)}
                    disabled={!group.dirty}
                    title="Lagre"
                  >
                    <Save className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteGroup(idx)}
                    className="text-admin-negative hover:text-admin-negative"
                    title="Slett gruppe"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <RangeRow
                  label="Standard"
                  min={group.normalMin}
                  max={group.normalMax}
                  onMinChange={(v) => update(idx, { normalMin: v })}
                  onMaxChange={(v) => update(idx, { normalMax: v })}
                />
                <RangeRow
                  label="Optimal"
                  min={group.optimalMin}
                  max={group.optimalMax}
                  onMinChange={(v) => update(idx, { optimalMin: v })}
                  onMaxChange={(v) => update(idx, { optimalMax: v })}
                  accent
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function RangeRow({
  label,
  min,
  max,
  onMinChange,
  onMaxChange,
  accent,
}: {
  label: string;
  min: string;
  max: string;
  onMinChange: (v: string) => void;
  onMaxChange: (v: string) => void;
  accent?: boolean;
}) {
  const hasValue = min !== "" || max !== "";
  return (
    <div className="flex items-center gap-3">
      <span
        className={`text-sm w-20 ${
          accent ? "text-admin-primary font-medium" : "text-admin-text-secondary"
        }`}
      >
        {label}
      </span>
      <Input
        type="number"
        step="any"
        value={min}
        onChange={(e) => onMinChange(e.target.value)}
        placeholder="min"
        className="w-24"
      />
      <span className="text-admin-text-subtle">—</span>
      <Input
        type="number"
        step="any"
        value={max}
        onChange={(e) => onMaxChange(e.target.value)}
        placeholder="max"
        className="w-24"
      />
      {!hasValue && (
        <span className="text-xs text-admin-text-subtle italic">ikke satt</span>
      )}
    </div>
  );
}
